<!-- Reconnect of an already-onboarded tenant: the picker and the analysing sequence are both
     skipped, so this is the only thing on screen while the saved company is re-verified. -->
<div class="sage-page" *ngIf="reconnecting">
  <div class="analysing-card">
    <div class="spinner"></div>
    <h2 class="analysing-title">Reconnecting to Sage</h2>
    <p class="analysing-sub">Picking up where you left off.</p>
  </div>
</div>

<div class="sage-page" *ngIf="!loadingStatus && !reconnecting">
  <div class="page-header">
    <div>
      <h1 class="page-title">Sage Integration</h1>
      <p class="page-subtitle">Connect your Sage company to AIVOR</p>
    </div>
    <div class="user-block" *ngIf="fullName">
      <span class="user-label">Current User:</span>
      <div class="user-name">{{ fullName }}</div>
      <div class="user-tenant">{{ tenantName }}</div>
    </div>
  </div>

  <div class="alert-banner" *ngIf="errorMessage">
    <i class="bi bi-exclamation-circle"></i>
    {{ errorMessage }}
  </div>

  <!-- Connecting / analysing state -->
  <div class="analysing-card" *ngIf="connecting">
    <div class="spinner"></div>
    <h2 class="analysing-title">Analysing your Sage company</h2>
    <p class="analysing-sub">This usually takes under a minute. Nothing is written yet.</p>
    <div class="analysing-steps">
      <div class="analysing-step" *ngFor="let step of analysingSteps">
        <i class="bi" [class.bi-circle]="!step.done" [class.bi-check-circle-fill]="step.done" [class.done]="step.done"></i>
        <span>{{ step.label }}</span>
      </div>
    </div>
  </div>

  <ng-container *ngIf="!connecting">
    <!-- Hero connect card -->
    <div class="hero-card">
      <div class="hero-logos">
        <div class="logo-tile aivor-tile">A</div>
        <div class="logo-arrow">
          <i class="bi bi-arrow-left-right"></i>
          <span>two-way</span>
        </div>
        <div class="logo-tile sage-tile">sage</div>
      </div>
      <div class="hero-body">
        <div class="hero-title-row">
          <h2 class="hero-title">Sage Business Cloud Accounting</h2>
          <span class="pill pill-neutral">{{ previouslyLinkedCompany ? 'Disconnected' : 'Not connected' }}</span>
        </div>
        <p class="hero-copy" *ngIf="previouslyLinkedCompany">
          {{ previouslyLinkedCompany }} is still linked to AIVOR. Reconnect to resume syncing — your
          matches and review history are exactly as you left them.
        </p>
        <p class="hero-copy" *ngIf="!previouslyLinkedCompany">
          Link your Sage company so suppliers, categories and items stay in step with AIVOR. Nothing
          is written to Sage until you have reviewed and approved every match in the next step.
        </p>
        <button class="btn btn-teal-gradient" (click)="onConnectClick()">
          <i class="bi bi-plug"></i> {{ previouslyLinkedCompany ? 'Reconnect to Sage' : 'Connect to Sage' }}
        </button>
      </div>
    </div>

    <!-- Info cards -->
    <div class="info-grid">
      <div class="info-card" [style.border-top-color]="entityAccentForType(infoCards[0].entityType)">
        <div class="info-icon" [style.background]="entityAccentSoftForType(infoCards[0].entityType)" [style.color]="entityAccentForType(infoCards[0].entityType)">
          <i class="bi" [ngClass]="infoCards[0].icon"></i>
        </div>
        <h3 class="info-title">What gets synced</h3>
        <ul class="info-checklist">
          <li><i class="bi bi-check2"></i> Suppliers and their categories</li>
          <li><i class="bi bi-check2"></i> Items and their categories</li>
          <li><i class="bi bi-check2"></i> Finished goods and their bills of materials</li>
        </ul>
        <p class="info-footnote">Each finished good goes to Sage either as a bundle that lists what it's made from, or as one item with those costs added together. Manufacturing orders stay in AIVOR only.</p>
      </div>

      <div class="info-card" [style.border-top-color]="entityAccentForType(infoCards[1].entityType)">
        <div class="info-icon" [style.background]="entityAccentSoftForType(infoCards[1].entityType)" [style.color]="entityAccentForType(infoCards[1].entityType)">
          <i class="bi" [ngClass]="infoCards[1].icon"></i>
        </div>
        <h3 class="info-title">How records are matched</h3>
        <p class="info-copy">
          Records are matched exactly on email, code or name first. Anything left over is scored by
          name similarity.
        </p>
        <div class="threshold-pills">
          <span class="pill pill-success">&ge; {{ autoThreshold }}% auto-confirmed</span>
          <span class="pill pill-warning">{{ reviewThreshold }}&ndash;{{ autoThreshold }}% needs review</span>
          <span class="pill pill-neutral">&lt; {{ reviewThreshold }}% no match</span>
        </div>
      </div>

      <div class="info-card" [style.border-top-color]="entityAccentForType(infoCards[2].entityType)">
        <div class="info-icon" [style.background]="entityAccentSoftForType(infoCards[2].entityType)" [style.color]="entityAccentForType(infoCards[2].entityType)">
          <i class="bi" [ngClass]="infoCards[2].icon"></i>
        </div>
        <h3 class="info-title">What happens next</h3>
        <ol class="info-steps">
          <li><span class="step-num">1</span> We analyse your Sage company and AIVOR records.</li>
          <li><span class="step-num">2</span> You review and confirm every match.</li>
          <li><span class="step-num">3</span> AIVOR runs the initial sync once everything is confirmed.</li>
        </ol>
        <p class="info-footer-note"><i class="bi bi-clock-history"></i> Your progress is saved as you go - you can leave and come back.</p>
      </div>
    </div>
  </ng-container>

  <!-- Company picker modal -->
  <div class="cp-overlay" *ngIf="showCompanyPicker">
    <div class="cp-panel">
      <div class="cp-header">
        <div class="cp-header-tile">sage</div>
        <div>
          <h2 class="cp-title">Choose your Sage company</h2>
          <p class="cp-sub">
            Your Sage account has more than one company. Everything AIVOR reads or writes happens
            inside the one you pick here.
          </p>
        </div>
        <button class="cp-close" type="button" aria-label="Close" (click)="cancelCompanyPicker()">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <div class="cp-error cp-error-standalone" *ngIf="missingCompanyWarning">
        <i class="bi bi-exclamation-triangle"></i>
        {{ missingCompanyWarning }}
      </div>

      <div class="cp-loading" *ngIf="companiesLoading">
        <div class="cp-spinner"></div>
        <span>Fetching your companies from Sage&hellip;</span>
      </div>

      <div class="cp-error cp-error-standalone" *ngIf="!companiesLoading && companiesError">
        <i class="bi bi-exclamation-circle"></i>
        {{ companiesError }}
        <button class="btn btn-teal-gradient" (click)="loadCompanies()">Retry</button>
      </div>

      <ng-container *ngIf="!companiesLoading && !companiesError">
        <div class="cp-list">
          <div
            class="cp-option"
            *ngFor="let company of companies"
            [class.selected]="selectedCompanyId === (company.id + '')"
            (click)="selectCompanyOption(company)"
          >
            <i
              class="bi"
              [class.bi-circle]="selectedCompanyId !== (company.id + '')"
              [class.bi-record-circle-fill]="selectedCompanyId === (company.id + '')"
            ></i>
            <div class="cp-option-body">
              <div class="cp-option-name">{{ company.name }}</div>
              <div class="cp-option-meta">Sage ID {{ company.id }}</div>
            </div>
            <span class="cp-pill-linked" *ngIf="isAlreadyLinked(company)">Already linked</span>
          </div>
        </div>

        <p class="cp-footnote">
          <i class="bi bi-info-circle"></i>
          You can change this later, but matching then starts over.
        </p>

        <div class="cp-error cp-error-inline" *ngIf="saveCompanyError">
          <i class="bi bi-exclamation-circle"></i>
          {{ saveCompanyError }}
        </div>

        <div class="cp-footer">
          <button class="btn cp-btn-cancel" (click)="cancelCompanyPicker()">Cancel</button>
          <button
            class="btn cp-btn-continue"
            [disabled]="!selectedCompanyId || savingCompany"
            (click)="confirmCompanySelection()"
          >
            {{ savingCompany ? 'Saving…' : 'Continue' }}
            <i class="bi bi-arrow-right"></i>
          </button>
        </div>
      </ng-container>
    </div>
  </div>
</div>
